import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { addDoctorNotesByVisitIdApi } from "../../../services/consultation.service";
import { postRequest } from "../../../services/api.request";

const AddNoteModal = ({ open, onClose, id }) => {
  const initialState = {
    note: "",
    fileUrl: "",
  };
  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevValue) => ({
      ...prevValue,
      [name]: value,
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append("type", "patient-history");
      uploadData.append("image", file);
      const response = await postRequest("/user/v1/image", uploadData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response?.code === 200) {
        setFormData((prev) => ({ ...prev, fileUrl: response?.data?.Location }));
        setFileName(file.name);
        toast.success("File uploaded successfully");
      } else {
        toast.error(response?.message || "Failed to upload file");
      }
    } catch {
      toast.error("Failed to upload file");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleRemoveFile = () => {
    setFormData((prev) => ({ ...prev, fileUrl: "" }));
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submitHander = async () => {
    if (id) {
      let response;
      setLoading(true);
      try {
        response = await addDoctorNotesByVisitIdApi(id, formData);
        if (response.code === 200 || response.code === 201) {
          toast.success("Note added successfully");
          onClose();
          setLoading(false);
          setFormData(initialState);
          setFileName("");
        } else {
          toast.error(response.message || "Some error occurred!");
        }
      } catch (error) {
        setLoading(false);
        console.error(error);
        toast.error(error);
      }
    } else {
      toast.error("Invalid visit ID!");
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: "#E5F4FF",
        },
      }}
    >
      <DialogContent>
        {/* dialog title */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: "20px",
          }}
        >
          <Typography variant="h6" sx={{ color: "#5D6A82" }}>
            Add Note
          </Typography>
          <Close
            onClick={onClose}
            sx={{ cursor: "pointer", color: "#5D6A82" }}
          />
        </Box>

        <Grid
          container
          spacing={3}
          sx={{
            mb: "40px",
          }}
        >
          <Grid item xs={12}>
            <TextField
              multiline
              rows={3}
              label="Enter Brief Description"
              required
              name="note"
              value={formData?.note || ""}
              onChange={handleChange}
              fullWidth
              sx={{ background: "white", borderRadius: "12px" }}
            />
          </Grid>

          <Grid item xs={12}>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              style={{ display: "none" }}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            />
            <TextField
              label="Upload Patient History"
              value={fileName}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    {fileName ? (
                      <Box
                        onClick={handleRemoveFile}
                        sx={{
                          bgcolor: "#FDECEA",
                          color: "error.main",
                          width: "130px",
                          height: "55px",
                          display: "grid",
                          placeItems: "center",
                          borderLeft: "1px solid #CCD4DA",
                          cursor: "pointer",
                        }}
                      >
                        Remove
                      </Box>
                    ) : (
                      <Box
                        onClick={() => !uploading && fileInputRef.current?.click()}
                        sx={{
                          bgcolor: "#F0F7FC",
                          color: "primary.main",
                          width: "130px",
                          height: "55px",
                          display: "grid",
                          placeItems: "center",
                          borderLeft: "1px solid #CCD4DA",
                          cursor: uploading ? "wait" : "pointer",
                        }}
                      >
                        {uploading ? <CircularProgress size={20} /> : "Upload"}
                      </Box>
                    )}
                  </InputAdornment>
                ),
                sx: {
                  bgcolor: "white",
                  pr: 0,
                },
              }}
            />
          </Grid>
        </Grid>

        {/* submit button */}
        <Box sx={{ my: "20px", textAlign: "center" }}>
          <Button
            variant="contained"
            sx={{ width: "150px", textTransform: "none" }}
            onClick={submitHander}
            disabled={loading || uploading}
          >
            {loading ? <CircularProgress size={"20px"} /> : "Save"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AddNoteModal;
